import {createStore, filterNil} from '@ngneat/elf';
import {deleteEntities, selectEntity, selectManyByPredicate, updateEntities, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {computed, Injectable, Signal, signal} from "@angular/core";
import {FeilhåndteringArgumenter, Feilhåndteringsstrategi, FeilhåndteringsstrategiForDataOgMeta, hentFraLokallager, hentFraLokallagerEllerNull, håndterRequestfeilViaCatchError, Leveringsvalg, opprettEllerOppdatertHendelse, registrerEntiteterMedForelder, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, serverFraRequestPakketIMeta, slettHendelse, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AgendaelementModell, GruppeId, LitenIkkeTomStreng, Modelloppdatering, ModelloppdateringType, MøtedeltakereModell, MøtedeltakerId, MøtedeltakerRessursClient, MøteId, MøteModell, MøteModellEnkel, MøteModellEnkelMedProtokollinfo, MøtepubliseringSpesifikasjon, MøtePubliseringValideringsfeil, MøteRessursClient, NyttMøteSpesifikasjon, OppdaterMøteSpesifikasjon, OppdaterSaksrekkefølgeSpesifikasjon, PlanId, ProtokollRessursClient, SendInnkallingSpesifikasjon, SendProtokollsigneringsInvitasjonSpesifikasjon, SigneringStatus, SkjermingDetaljerModell, SkjermingModell} from "../ressurser/generertNg2HttpKlient";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {erIkkeNil, erNil} from "../../støttefunksjoner/nilsjekk";
import {firstValueFrom, Observable} from "rxjs";
import {DataOgMeta, kunRequestRespons, mapTilData} from "./DataOgMeta";
import {HttpEvent, HttpEventType, HttpResponse} from "@angular/common/http";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {MøtedeltakereDatatjeneste} from "felles-ng2/tjenester/datalager/MøtedeltakereDatatjeneste";
import {map, tap} from "rxjs/operators";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {formDataMedFil} from "felles-ng2/støttefunksjoner/dom";
import {sorterLevertSamling, utelatFørsteDersomNil} from "felles/ng2/rxjsHjelpere";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {i18nArgumenterMedAnsvarligBruker} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {lesMøtedato2, sorterMøterPåDato} from "felles-ng2/modell/møteModellHjelpere";
import {TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {MøteMedAggregaterDatatjeneste} from "felles-ng2/tjenester/datalager/MøteMedAggregaterDatatjeneste";
import {Modelloppdateringer, OppdateringmodellMøteOppdatert, OppdateringmodellMøteSlettet, OppdateringmodellSaksrekkefølgeOppdatert} from "felles-ng2/tjenester/Modelloppdateringer";

const debug = initDebugger('MøteDatatjeneste');

/** Møte forelder */
export type Forelder = PlanId;

/** Datalager for {@link MøteModell} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class MøteDatatjeneste {

    /** Aktiv møte */
    private readonly _aktivMøteId = signal<MøteId | null>(null);
    private readonly _erAktivMøteSatt = computed(() => erIkkeNil(this.aktivMøteIdSomSignal()));

    /** Lager for {@link MøteModell}. Bruker {@link PlanId} som forelder */
      //public for testbarhet
    readonly møteLager = createStore({name: 'møte'}, withEntities<MøteModell>());

    /** Lager for {@link MøteModellEnkel}. Bruker {@link PlanId} som forelder */
      //public for testbarhet
    readonly møteEnkelLager = createStore({name: 'møteEnkel'}, withEntities<MøteModellEnkel>());

    /** Holder rede på åpne subscriptions basert på møteId. Benyttes ifbm modelloppdateringer. */
      //public for testbarhet
    readonly møteAbonnenter = new DatalagerAbonnenter('møte.id');

    constructor(private møteRessurs: MøteRessursClient,
                private møtedeltakerRessurs:MøtedeltakerRessursClient,
                private protokollRessurs: ProtokollRessursClient,
                private sakDatatjenester: SakDatatjeneste,
                private møtedeltakereDatatjeneste: MøtedeltakereDatatjeneste,
                private møteMedAggregaterDatatjeneste: MøteMedAggregaterDatatjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdatering: Modelloppdateringer) {

        this.propagerEndringerTilAndreDatatjenester();
        this.lyttTilModelloppdateringer();
    }

    get aktivMøteId(): MøteId | null {
        return this._aktivMøteId();
    }

    get aktivMøteIdSomSignal(): Signal<MøteId | null> {
        return this._aktivMøteId;
    }

    setAktivMøte(id: MøteId) {
        this._aktivMøteId.set(id);
    }

    get erAktivMøteSatt(): boolean {
        return this.erAktivMøteSattSomSignal();
    }

    get erAktivMøteSattSomSignal(): Signal<boolean> {
        return this._erAktivMøteSatt;
    }

    resetAktivMøte() {
        this._aktivMøteId.set(null);
    }

    /** Returnerer møte fra lokallager.
     * Forutsetter at møte er lastet tidligere. */
    hentFraFraLokallager(id: MøteId): MøteModell {
        return hentFraLokallager(this.møteLager, id);
    }

    /** Returnerer sann dersom møte eksisterer i lokallager. */
    eksistererILokallager(id: MøteId): boolean {
        return hentFraLokallagerEllerNull(this.møteLager, id) != null;
    }

    hentFraFraLokallagerSomObservable(møteId: MøteId): Observable<MøteModell> {
        debug('hentFraFraLokallagerSomObservable', møteId, this.møteAbonnenter.antallAbonnenter(møteId))
        return this.møteAbonnenter.observer(møteId,
          this.møteLager.pipe(
            selectEntity(møteId),
            filterNil(),
            distinctUntilChangedDeep())); //kun lever verdier fra lageret ved reelle endringer;
    }

    /** Erstatter angitt møte i lokalt datalager */
    erstattMøteILokallager(møte: MøteModell): MøteModell {
        return registrerEntitetMedForelder(this.møteLager, møte, this.forelderIdFraMøte(møte));
    }

    /** Erstatter angitt møte i lokalt datalager for enkel møtemodell */
    erstattMøteEnkelILokallager(møte: MøteModellEnkel): MøteModellEnkel {
        return registrerEntitetMedForelder(this.møteEnkelLager, møte, this.forelderIdFraMøte(møte));
    }

    /** Laster angitt møte, returnerer enkel møtemodell.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hentEnkelMedMeta(id: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteModellEnkel>>;
    hentEnkelMedMeta(id: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteModellEnkel | undefined>>;
    hentEnkelMedMeta(id: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteModellEnkel | undefined>> {
        return this.møteAbonnenter.observer(id,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.møteRessurs.hentEnkel(id),
              registrerILager: (respons: HttpResponse<MøteModellEnkel>) => this.erstattMøteEnkelILokallager(respons.body!),
              hentFraLager: () => this.møteEnkelLager.pipe(
                selectEntity(id),
                leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente møte', 'hentMedMeta')
          }));
    }

    /** Laster angitt møte, returnerer enkel møtemodell.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hentEnkel(id: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<MøteModellEnkel>;
    hentEnkel(id: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<MøteModellEnkel | undefined>;
    hentEnkel(id: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<MøteModellEnkel | undefined> {
        return this.hentEnkelMedMeta(id, leveringsvalg, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Laster angitt møte.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    //@todo returner MøteModellDLM
    hentMedMeta(id: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteModell>>;
    hentMedMeta(id: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteModell | undefined>>;
    hentMedMeta(id: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteModell | undefined>> {
        return this.møteAbonnenter.observer(id,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.møteRessurs.hent(id),
              registrerILager: (respons: HttpResponse<MøteModell>) => this.erstattMøteILokallager(respons.body!),
              hentFraLager: () => this.møteLager.pipe(
                selectEntity(id),
                leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente møte', 'hentMedMeta')
          }));
    }

    /** Laster angitt møte.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    //@todo returner MøteModellDLM
    hent(id: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<MøteModellDLM>;
    hent(id: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<MøteModellDLM | undefined>;
    hent(id: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<MøteModellDLM | undefined> {
        return this.hentMedMeta(id, leveringsvalg, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Laster angitt møte fra server og oppdaterer lokalt datalager med dette. */
    async relast(id: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<void> {
        await firstValueFrom(this.hentMedMeta(id, Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi).pipe(kunRequestRespons()));
    }

    /** Returnerer informasjon om protokoll for møter med startdato i angitt periode. Eldste møte først.  */
    finnMøterOgProtokollbehandlinger(gruppeId: GruppeId, periode: ZonedDateTimePeriodeLukket, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteModellEnkelMedProtokollinfo[]>> {
        let sistReturnerteMøter = new Map<MøteId, MøteModellEnkelMedProtokollinfo>;
        const data: Observable<DataOgMeta<MøteModellEnkelMedProtokollinfo[]>> = serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.møteRessurs.finnMoeterEnkelMedProtokollinfoIPerioden({grupper: [gruppeId], ...periode.somSerialiserbar()}),
            registrerILager: respons => {
                const møterMedInfo = respons.body!.treff;
                møterMedInfo.forEach(møteinfo => sistReturnerteMøter.set(møteinfo.moete.id, møteinfo));
                registrerEntiteterMedForelder(this.møteEnkelLager, møterMedInfo.map(møteinfo => møteinfo.moete), møte => this.forelderIdFraMøte(møte));
                //finnMoeterEnkelMedProtokollinfoIPerioden returnerer liste med eldste møte sist, vi forventer eldste først
                return møterMedInfo.reverse();
            },
            hentFraLager: () => {
                return this.møteEnkelLager.pipe(
                  selectManyByPredicate(møte => møte.eiendeGruppeId == gruppeId && lesMøtedato2(møte).overlapper(periode)),
                  sorterLevertSamling(sorterMøterPåDato),
                  map(møter => møter.map(møte => ({
                      //henter møte fra datalager
                      moete: møte,
                      //henter øvrige verdier fra serverrespons stuet unna tidligere, ellers standard verdier
                      protokolSigneringStatus: sistReturnerteMøter.get(møte.id)?.protokolSigneringStatus ?? SigneringStatus.ikkeAktivert,
                      protokollErTilBehandling: sistReturnerteMøter.get(møte.id)?.protokollErTilBehandling ?? false,
                      protokollBehandlesI: sistReturnerteMøter.get(møte.id)?.protokollBehandlesI ?? [],
                  }) as MøteModellEnkelMedProtokollinfo))
                );
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente møter")), 'finnMøterOgProtokollbehandlinger')
        });
        // return this.periodeAbonnenter.observer(KommentarDatatjeneste.periodeAbonnentNøkkel, data);

        return data;
    }

    opprett(spesifikasjon: NyttMøteSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<MøteModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.møteRessurs.opprett(spesifikasjon).råRespons()
              .pipe(tap(respons => registrerEntitetMedForelder(this.møteLager, respons.body!, this.forelderIdFraMøte(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke opprette møte')), 'opprett')
        }));
    }

    oppdaterSaksrekkefølge(id: MøteId, spesifikasjon: OppdaterSaksrekkefølgeSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<MøteModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.møteRessurs.oppdaterSaksrekkefølge(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.erstattMøteILokallager(respons.body!))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere saksrekkefølge')), 'oppdaterSaksrekkefølge')
        }));
    }

    /** Endrer publiseringsstatus, relaster møtet dersom operasjonen ikke førte til valideringsfeil.
     * Valideringsfeil er kun relevant dersom møtet av-publiseres, og må i så tilfelle håndteres av kaller. */
    async oppdaterMøtePublisering(id: MøteId, spesifikasjon: MøtepubliseringSpesifikasjon, feilhåndteringsstrategi = Feilhåndteringsstrategi.propagerSomError): Promise<TypetValideringsresultatModell<MøtePubliseringValideringsfeil>> {
        const valideringsresultat = await this.møteRessurs.oppdaterPublisert(id, spesifikasjon)
          .somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke publisere møtet')) as TypetValideringsresultatModell<MøtePubliseringValideringsfeil>;
        if (valideringsresultat.harFeil) return valideringsresultat;

        //dersom ingen valideringsfeil, relast møte slik at endringen i publiseringsstatus blir synlig for abonnenter
        await this.relast(id);
        return valideringsresultat;
    }

    oppdaterProtokollPublisering(id: MøteId, spesifikasjon: MøtepubliseringSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<MøteModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.protokollRessurs.oppdaterPublisering(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.erstattMøteILokallager(respons.body!))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere publiseringsstatus for protokoll')), 'oppdaterProtokollPublisering')
        }));
    }

    sendProtokollTilSignering(id: MøteId, spesifikasjon: SendProtokollsigneringsInvitasjonSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<MøteModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.protokollRessurs.sendTilSignering(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.erstattMøteILokallager(respons.body!))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke sende protokoll til signering')), 'sendProtokollTilSignering')
        }));
    }

    låsOppProtokoll(id: MøteId, årsak: LitenIkkeTomStreng, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<MøteModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.protokollRessurs.låsOppProtokoll(id, {aarsak: årsak}).råRespons()
              .pipe(tap(respons => this.erstattMøteILokallager(respons.body!))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke låse opp protokoll')), 'låsOppProtokoll')
        }));
    }

    registrerEksterntSignertProtokoll(id: MøteId, signertProtokoll: File, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<HttpEvent<MøteModell>> {
        return this.protokollRessurs.registrerSignertProtokolldokument(id, formDataMedFil(signertProtokoll)).råEventer()
          .pipe(
            tap<HttpEvent<MøteModell>>(event => {
                if (event.type == HttpEventType.Response) this.erstattMøteILokallager(event.body!);
            }),
            håndterRequestfeilViaCatchError(this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('En feil oppstod under registrering av signert protokoll')), 'registrerEksterntSignertProtokoll'))
          );
    }

    oppdater(id: MøteId, spesifikasjon: OppdaterMøteSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<MøteModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.møteRessurs.oppdater(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.erstattMøteILokallager(respons.body!))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere møte')), 'oppdater')
        }));
    }

    /** Oppdaterer angitt møte i lokallager med verdier angitt i `oppdatering`. */
    oppdaterILokallager(møteId: MøteId, oppdatering: Partial<MøteModell>) {
        this.møteLager.update(updateEntities(møteId, oppdatering));
    }

    /** Oppdaterer angitt møte i lokallager med angitt skjermingsinformasjon. */
    oppdaterSkjermingILokallager(møteId: MøteId, skjerming: SkjermingModell) {
        const møte = hentFraLokallagerEllerNull(this.møteLager,  møteId);
        if (erNil(møte)) return;
        this.oppdaterILokallager(møteId, {tilgang: {...møte.tilgang, skjerming: skjerming}});
    }

    async slett(id: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.møteRessurs.slett(id).råRespons()
              .pipe(tap(respons => this.møteLager.update(deleteEntities(id)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette møte')), 'slett')
        }));
    }

    async sendInnkalling(id: MøteId, spesifikasjon: SendInnkallingSpesifikasjon): Promise<MøtedeltakereModell> {
        const møtedeltakere = await this.møteRessurs.sendInnkallingV2(id, spesifikasjon).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke sende innkalling'))
        await this.relast(id);
        return møtedeltakere;
    }

    async sendPurring(id: MøteId): Promise<MøtedeltakereModell> {
        const møtedeltakere = await this.møteRessurs.sendPurring(id).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke sende purring'))
        await this.relast(id);
        return møtedeltakere;
    }

    godtaInnkalling(møteId: MøteId) {
        this.møtedeltakerRessurs.aksepterInnkalling$POST$moetedeltaker_id_aksepterInnkalling(møteId).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke godta innkalling'))
          .then(oppdatertDeltaker => this.modelloppdatering.broadcastLokalMøtedeltakerOppdatert(oppdatertDeltaker.id, møteId))
          .then(() => this.relast(møteId));
    }

    avslåInnkalling(møteId: string) {
        this.møtedeltakerRessurs.avslåInnkalling$POST$moetedeltaker_id_avslaaInnkalling(møteId).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke avslå innkalling'))
          .then(oppdatertDeltaker => this.modelloppdatering.broadcastLokalMøtedeltakerOppdatert(oppdatertDeltaker.id, møteId))
          .then(() => this.relast(møteId));
    }

    //hjelpemetoder

    private forelderIdFraMøte(møte: Pick<MøteModellEnkel, 'planId'>) {
        return møte.planId;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /** Ved endringer i møte må lager som håndterer data inkludert i møte oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.møteLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer møteLager ${hendelse.type} til møteEnkelLager, SakDatatjeneste og MøtedeltakereDatatjeneste for møtene ${hendelse.ids}`, hendelse, entiteter);
            entiteter.forEach(møte => {
                this.møteEnkelLager.update(upsertEntities(møte));
                this.sakDatatjenester.erstattSakerILokallager(møte.id, møte.saker);
                this.møtedeltakereDatatjeneste.erstattDeltakereTilknyttetMøteILokallager(møte.deltakere, møte.id);
                this.møteMedAggregaterDatatjeneste.oppdaterMøteIILokallager(møte);
            });
        });

        slettHendelse(this.møteLager).subscribe(hendelse => {
            debug(`propagerer møteLager ${hendelse.type} til møteEnkelLager, SakDatatjeneste og MøtedeltakereDatatjeneste for møtene ${hendelse.ids}`);
            this.møteEnkelLager.update(deleteEntities(hendelse.ids));
            this.sakDatatjenester.slettSakerTilknyttetMøterILokallager(hendelse.ids);
            this.møtedeltakereDatatjeneste.slettDeltakereTilknyttetMøterILokallager(hendelse.ids);
            this.møteMedAggregaterDatatjeneste.slettMøteIILokallager(hendelse.ids);
        });
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        //lytter ikke til ModelloppdateringType.NYTT_MØTE da vi ikke har noe abonnement basert på en 'forelder' (vi har kun abonnement på møte.id,
        this.lyttTilMøteOppdatert();
        this.lyttTilMøteSlettet();
        this.lyttTilSaksrekkefølgeOppdatert();
        this.lyttTilMøtedeltakere();
        this.lyttTilMøtedeltaker();
    }

    private lyttTilMøteOppdatert() {
        this.opprettetEllerOppdatertMøteHendelse<OppdateringmodellMøteOppdatert>(ModelloppdateringType.MØTE_OPPDATERT)
          .medAbonnentoppslag(this.møteAbonnenter, møteId => møteId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Møte "{møte}" oppdatert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteSlettet() {
        this.slettetHendelse<OppdateringmodellMøteSlettet>(ModelloppdateringType.MØTE_SLETTET)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Møte "{møte}" slettet (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilSaksrekkefølgeOppdatert() {
        this.opprettetEllerOppdatertMøteHendelse<OppdateringmodellSaksrekkefølgeOppdatert>(ModelloppdateringType.SAKSREKKEFØLGE_OPPDATERT)
          .medAbonnentoppslag(this.møteAbonnenter, møteId => møteId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Saksrekkefølge har blitt oppdatert (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøtedeltaker() {
        new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<OppdateringmodellMøtedeltaker, MøteModell>(this.møteLager, this.modelloppdatering)
          .lyttTil(ModelloppdateringType.MØTEDELTAKER_OPPDATERT)
          .medEntitetId(møtedeltaker => møtedeltaker.moeteId)
          .medEntitetLaster(møtedeltaker => this.møteRessurs.hent(møtedeltaker.moeteId).råRespons())
          .medAbonnentoppslag(this.møteAbonnenter, møtedeltaker => møtedeltaker.moeteId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Møtedeltaker har blitt oppdatert (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøtedeltakere() {
        new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<OppdateringmodellMøtedeltakere, MøteModell>(this.møteLager, this.modelloppdatering)
          .lyttTil(ModelloppdateringType.MØTEDELTAKERE_OPPDATERT)
          .medEntitetId(møteId => møteId)
          .medEntitetLaster(møteId => this.møteRessurs.hent(møteId).råRespons())
          .medAbonnentoppslag(this.møteAbonnenter, møteId => møteId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Møtedeltakere har blitt oppdatert (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private slettetHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedMøte>(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, MøteModell>(this.møteLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(møteId => møteId);
    }

    private opprettetEllerOppdatertMøteHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedMøte>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, MøteModell>(this.møteLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(møteId => møteId)
          .medEntitetLaster(møteId => this.møteRessurs.hent(møteId).råRespons());
    }

    /** returnerer objekt med 'møte' og 'bruker' felt populert fra angitt oppdatering  */
    private i18nArgumenter(oppdatering: Modelloppdatering<any>, møteId: MøteId){
        return i18nArgumenterMedAnsvarligBruker(oppdatering, {møte: this.møtetittel(møteId)});
    }

    /** Returnerer 'møte.tittel møte.nummer' for møte registrert i møteLager, '...' dersom møtet ikke finnes i møteLager */
    private møtetittel(møteId: MøteId): string {
        const møte = this.møteLager.value.entities[møteId];
        if (erNil(møte)) return '...'
        return `${møte.tittel} ${møte.nummer}`;
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }

}

type OppdateringmodellMedMøte = MøteId;
type OppdateringmodellMøtedeltakere = MøteId;
type OppdateringmodellMøtedeltaker  = {
    moeteId:MøteId,
    moetedeltakerId: MøtedeltakerId
}

/** Datalager-spesifikk representasjon av et møte der felter som inneholder data som håndteres av andre datalager er fjernet.
 * Dette da datalageret ikke garanterer at disse feltene er oppdatert.
 * Verdier for disse feltene må hentes via de respektive datalagrene for de aktuelle dataene. */
export type MøteModellDLM = Omit<MøteModell, 'saker' | 'deltakere'>;
